import { type ReactNode, useEffect } from "react";
import { X } from "lucide-react";
import { cn } from "@/lib/utils/cn";
import { COMMISSION_STATUS, money, TONE_CLASS } from "./format";
import type { CommissionLine } from "./types";

/**
 * Piezas chicas compartidas por "Reclutadores" (Mixcore) y "Mi trabajo"
 * (reclutador). Todo con tokens --nx-* vía clases de Tailwind; nada de hex.
 * Texto mínimo 12 px (text-xs): el reclutador lo usa en el teléfono.
 */

export function Badge({ tone, children }: { tone: keyof typeof TONE_CLASS; children: ReactNode }) {
  return <span className={cn(TONE_CLASS[tone], "text-[11px] leading-4 normal-case tracking-normal")}>{children}</span>;
}

export type TabDef<T extends string> = { id: T; label: string; count?: number };

export function Tabs<T extends string>({ tabs, active, onChange }: { tabs: TabDef<T>[]; active: T; onChange: (id: T) => void }) {
  return (
    <div className="-mx-1 overflow-x-auto">
      <div role="tablist" className="flex gap-1 px-1 border-b border-border min-w-max">
        {tabs.map((t) => (
          <button
            key={t.id}
            type="button"
            role="tab"
            aria-selected={active === t.id}
            onClick={() => onChange(t.id)}
            className={cn(
              "px-3 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors whitespace-nowrap",
              active === t.id ? "border-accent text-fg" : "border-transparent text-fg-muted hover:text-fg",
            )}
          >
            {t.label}
            {t.count ? (
              <span className="ml-1.5 inline-flex min-w-5 h-5 px-1 items-center justify-center rounded-full bg-accent-soft text-accent text-xs tabular-nums">
                {t.count}
              </span>
            ) : null}
          </button>
        ))}
      </div>
    </div>
  );
}

export function Modal({
  title,
  icon,
  onClose,
  children,
  footer,
  wide,
}: {
  title: string;
  icon?: ReactNode;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  wide?: boolean;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center sm:p-4 bg-black/60" onClick={onClose} role="dialog" aria-modal="true">
      <div
        className={cn(
          "nx-card w-full max-h-[92vh] flex flex-col rounded-b-none sm:rounded-b-lg",
          wide ? "sm:max-w-2xl" : "sm:max-w-lg",
        )}
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-center justify-between gap-3 px-5 py-3 border-b border-border shrink-0">
          <div className="flex items-center gap-2 min-w-0">
            {icon}
            <h2 className="text-sm font-semibold text-fg truncate">{title}</h2>
          </div>
          <button onClick={onClose} className="text-fg-subtle hover:text-fg transition-colors p-1" aria-label="Cerrar">
            <X size={18} />
          </button>
        </header>
        <div className="overflow-y-auto px-5 py-4 flex flex-col gap-4">{children}</div>
        {footer && <footer className="px-5 py-3 border-t border-border flex flex-wrap justify-end gap-2 shrink-0">{footer}</footer>}
      </div>
    </div>
  );
}

export function Field({ label, hint, required, children }: { label: string; hint?: string; required?: boolean; children: ReactNode }) {
  return (
    <label className="block">
      <span className="text-xs text-fg-muted block mb-1">
        {label} {required && <span className="text-danger">*</span>}
      </span>
      {children}
      {hint && <span className="text-xs text-fg-subtle block mt-1">{hint}</span>}
    </label>
  );
}

/** Cifra destacada: etiqueta arriba, número grande, nota abajo. */
export function Stat({ label, value, note, tone }: { label: string; value: ReactNode; note?: ReactNode; tone?: "accent" | "success" | "warning" | "danger" }) {
  return (
    <div className="nx-card p-4 min-w-0">
      <div className="text-xs text-fg-muted">{label}</div>
      <div
        className={cn(
          "mt-1 text-xl font-bold tabular-nums tracking-tight",
          tone === "accent" && "text-accent",
          tone === "success" && "text-success",
          tone === "warning" && "text-warning",
          tone === "danger" && "text-danger",
          !tone && "text-fg",
        )}
      >
        {value}
      </div>
      {note && <div className="mt-1 text-xs text-fg-subtle">{note}</div>}
    </div>
  );
}

export function SectionTitle({ children, action }: { children: ReactNode; action?: ReactNode }) {
  return (
    <div className="flex items-end justify-between gap-3">
      <h2 className="nx-h2 text-base">{children}</h2>
      {action}
    </div>
  );
}

export function Empty({ children }: { children: ReactNode }) {
  return <div className="nx-card p-6 text-center text-sm text-fg-muted">{children}</div>;
}

export function KeyValue({ rows }: { rows: Array<[string, ReactNode]> }) {
  return (
    <dl className="grid grid-cols-[minmax(0,42%)_minmax(0,1fr)] gap-x-3 gap-y-2 text-sm">
      {rows.map(([k, v]) => (
        <div key={k} className="contents">
          <dt className="text-fg-muted">{k}</dt>
          <dd className="text-fg min-w-0 break-words">{v ?? "—"}</dd>
        </div>
      ))}
    </dl>
  );
}

export function CommissionStatusBadge({ status }: { status: CommissionLine["status"] }) {
  const s = COMMISSION_STATUS[status];
  return (
    <span title={s.help}>
      <Badge tone={s.tone}>{s.label}</Badge>
    </span>
  );
}

/** "De dónde sale cada peso" de una comisión de evento (B3 del programa). */
export function CommissionBreakdownView({ line }: { line: CommissionLine }) {
  const b = line.breakdown;
  if (line.kind === "origination") {
    return <p className="text-xs text-fg-muted">Pago fijo, una vez por productor, cuando vende su primer evento en Mixcore.</p>;
  }
  if (line.kind === "adjustment") {
    return <p className="text-xs text-fg-muted">{line.description || "Ajuste manual de Mixcore."}</p>;
  }
  if (!b) return null;
  const rows: Array<[string, string, string?]> = [
    ["Comisión de servicio de Mixcore (con IVA)", money(b.commissionGross)],
  ];
  if (b.splitCommissionGross) rows.push(["+ Cobrado con Split Payment", money(b.splitCommissionGross)]);
  if (b.refundedAmount) rows.push(["− Reembolsos y anulados", money(b.refundedAmount)]);
  if (b.waivedAmount) rows.push(["− Cortesía de Mixcore", money(b.waivedAmount)]);
  rows.push(["= Lo que recibe Mixcore", money(b.receivedGross)]);
  rows.push(["− IVA", money(b.iva)]);
  rows.push(["Base neta", money(b.base), "font-semibold text-fg"]);
  rows.push([`× ${line.ratePct?.toLocaleString("es-CL") ?? "—"} % (${line.stage === "maintenance" ? "mantención" : "activación"}, evento n.º ${line.number ?? "—"})`, money(line.amount), "font-semibold text-accent"]);
  return (
    <div className="space-y-1.5 max-w-md">
      <table className="w-full text-xs">
        <tbody>
          {rows.map(([k, v, cls]) => (
            <tr key={k}>
              <td className={cn("py-0.5 pr-3 text-fg-muted", cls)}>{k}</td>
              <td className={cn("py-0.5 text-right tabular-nums whitespace-nowrap text-fg", cls)}>{v}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="text-xs text-fg-subtle">
        {b.source === "settlement"
          ? "Según la liquidación del evento."
          : "Estimado con las ventas actuales: se confirma cuando se concilia el evento."}
      </p>
    </div>
  );
}
